import { memo, useCallback } from 'react'
import {
  CrewAvatarSize,
  CrewAvatar,
} from 'components/elements/crewAvatar/crewAvatar'
import { generateImageAvatarUrl } from '@crew/utils/avatar'
import { EntityType } from '@crew/enums/domain'
import { useAppSelector } from 'states/hooks'
import { GetUsersRequest } from '@crew/apis/lookup/models/getUsers/request'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetLookupUsersQuery } from '@crew/apis/lookup/lookupApis'
import classNames from 'classnames'
import { CrewRadioGroup } from 'components/devextreme/crewRadioGroup'
import { ComponentCallbackHandler } from '@crew/utils'
import { UserRef } from '@crew/models/refs'

export type ChatStartUserTabUserListProps = {
  onUserSelected: (userId: string) => void
  isLoading: boolean
}

// ユーザータブのユーザー一覧
export const ChatStartUserTabUserList = memo(
  (props: ChatStartUserTabUserListProps) => {
    const keyword = useAppSelector(
      (state) => state.chatStartEntryDialog.userTabSearchKeyword
    )

    const loggedInUser = useAppSelector((state) => state.app.loggedInUser)

    // キーワードに該当するユーザ一覧取得
    // 三項演算子になっていて少し見づらいが、内部のパラメータがundefinedを受け付けないため三項演算子を使用している
    // undefined時はskipTokenでAPIを実行しない（キーワードテキストが空のとき）
    const getUsersParams: GetUsersRequest | undefined = keyword
      ? {
          keyword: keyword,
          userId: undefined,
        }
      : undefined

    const {
      data: getLookupUsersQueryResult,
      isFetching: isLookupUsersFetching,
    } = useGetLookupUsersQuery(getUsersParams ?? skipToken)

    const searchedUsers = getLookupUsersQueryResult?.users ?? []

    // 検索したユーザからログインユーザを省いてリスト出力
    const users = Object.values(searchedUsers).filter(
      (user) => user.id !== loggedInUser?.id
    )

    // ユーザー選択時
    const handleSelectUserChanged = useCallback<
      ComponentCallbackHandler<typeof CrewRadioGroup, 'onValueChanged'>
    >(
      (event) => {
        // 選択されたユーザーIDを親コンポーネントに通知
        props.onUserSelected(event.value)
      },
      [props]
    )

    // ラジオボタンの各ユーザー情報のrender
    const renderUserItem = useCallback(
      (user: UserRef) => {
        return (
          <div
            key={user.id} // Warning表示回避のkey指定
            className={classNames(
              'flex gap-2 py-1 w-[350px] border-b crew-border-gray ',
              props.isLoading && 'pointer-events-none opacity-[0.5]' // API実行中はクリックイベントを無効化
            )}
          >
            {/* ユーザーアバター */}
            <CrewAvatar
              displayName={user.displayName}
              imageURL={generateImageAvatarUrl(EntityType.User, user.id)}
              size={CrewAvatarSize.xs}
              cacheValue={user.id + user.version}
            />
            {/* ユーザー名 */}
            {/* NOTE: Linkコンポーネントだと遷移先（to）の設定が必要となるためここは文字色のみ他のLinkに合わせる */}
            <span className="text-crew-blue-3-light dark:text-crew-blue-2-dark max-w-xl truncate whitespace-nowrap overflow-hidden cursor-pointer">
              {user.displayName}
            </span>
          </div>
        )
      },
      [props.isLoading]
    )

    return (
      <>
        {users.length > 0 && (
          <CrewRadioGroup
            layout="vertical"
            disabled={isLookupUsersFetching} // フェッチ中は選択不可
            dataSource={users}
            valueExpr="id"
            displayExpr="displayName"
            itemRender={renderUserItem}
            onValueChanged={handleSelectUserChanged}
          />
        )}
      </>
    )
  }
)
